import React, { useEffect, useRef, useId } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Image from 'next/image';

const sanitize = (s: string) =>
  s.replace(/.\s*cite\s*(turn\d+\w+\s*)+/g, '')
   .replace(/.?cite(turn\d+\w+)+\b/g, '')
   .replace(/\bcite(turn\d+\w+)+\b/g, '')
   .replace(/\s*cite\s*(turn\d+\w+\s*)+/g, '')
   .replace(/image_group\s*\{[^{}]*\}/g, '')
   .replace(/entity\[[^\]]*\]/g, '')
   .replace(/ {2,}/g, ' ')
   .replace(/\n{3,}/g, '\n\n')
   .replace(/\. \./g, '.')
   .trim();

// Preserve legacy headings as H2: removing arbitrary headings loses content.

const sanitizeMermaid = (code: string) =>
  code.replace(/\[([^\]]*)\]/g, (_, inner) => {
    const fixed = inner.replace(/\n/g, '<br/>').replace(/"/g, '\\"');
    return /[()\n]/.test(inner) ? `["${fixed}"]` : `[${inner}]`;
  });

const canOptimizeMagImage = (src: string) => {
  if (src.startsWith('/')) return true;

  try {
    return new URL(src).hostname === 'mag-media.mynawoo.com';
  } catch {
    return false;
  }
};

function MermaidDiagram({ code }: { code: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const id = useId().replace(/:/g, '');

  useEffect(() => {
    if (!ref.current) return;
    import('mermaid').then(({ default: m }) => {
      m.initialize({ startOnLoad: false, theme: 'dark' });
      m.run({ nodes: [ref.current!], suppressErrors: true });
    }).catch(() => {});
  }, [code]);

  return (
    <div id={`mermaid-${id}`} ref={ref}
      className="mermaid my-4 flex justify-center [&_svg]:max-w-full [&_svg]:h-auto">
      {sanitizeMermaid(code)}
    </div>
  );
}

const cx = {
  h1: "text-4xl font-bold mb-6 mt-8 text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]",
  h2: "text-3xl font-bold mb-5 mt-7 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.25)]",
  h3: "text-2xl font-bold mb-4 mt-6 text-white/95",
  h4: "text-xl font-bold mb-3 mt-5 text-white/90",
  h5: "text-lg font-bold mb-2 mt-4 text-white/85",
  h6: "text-base font-bold mb-2 mt-3 text-white/80",
};

const components: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  h1: ({ node, ...p }) => <h2 className={cx.h2} {...p} />,
  h2: ({ node, ...p }) => <h2 className={cx.h2} {...p} />,
  h3: ({ node, ...p }) => <h3 className={cx.h3} {...p} />,
  h4: ({ node, ...p }) => <h4 className={cx.h4} {...p} />,
  h5: ({ node, ...p }) => <h5 className={cx.h5} {...p} />,
  h6: ({ node, ...p }) => <h6 className={cx.h6} {...p} />,
  p:  ({ node, ...p }) => <p className="mb-4 text-white-sec leading-relaxed" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc mb-4 space-y-2 text-white-sec ml-6" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal mb-4 space-y-2 text-white-sec ml-6" {...p} />,
  li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
  blockquote: ({ node, ...p }) => (
    <blockquote className="border-l-4 border-primary pl-4 py-2 my-4 bg-white/5 rounded-r-lg italic text-white-sec" {...p} />
  ),
  pre:  ({ node, ...p }) => <pre className="my-4 overflow-x-auto bg-white/5 rounded-lg border border-white/10" {...p} />,
  code: ({ node, className, children, ...p }: any) => {
    if (className?.includes('language-mermaid'))
      return <MermaidDiagram code={String(children).replace(/\n$/, '')} />;
    if (className)
      return <code className={`block text-white p-4 text-sm font-mono ${className}`} {...p}>{children}</code>;
    return <code className="bg-white/10 text-primary-shadow px-2 py-0.5 rounded text-sm font-mono" {...p}>{children}</code>;
  },
  table:  ({ node, ...p }) => <div className="overflow-x-auto my-6 rounded-lg border border-white/10 shadow-lg"><table className="min-w-full divide-y divide-white/10" {...p} /></div>,
  thead:  ({ node, ...p }) => <thead className="bg-white/10" {...p} />,
  tbody:  ({ node, ...p }) => <tbody className="bg-white/5 divide-y divide-white/5" {...p} />,
  tr:     ({ node, ...p }) => <tr className="hover:bg-white/5 transition-colors" {...p} />,
  th:     ({ node, ...p }) => <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider align-top" {...p} />,
  td:     ({ node, ...p }) => <td className="px-4 py-3 text-sm text-white-sec align-top break-words" {...p} />,
  a:      ({ node, ...p }) => <a className="text-primary-shadow hover:text-[#C4B5FD] underline transition-colors" target="_blank" rel="noopener noreferrer" {...p} />,
  hr:     ({ node, ...p }) => <hr className="my-8 border-t border-white/20" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-bold text-white" {...p} />,
  em:     ({ node, ...p }) => <em className="italic text-white-sec" {...p} />,
  img:    ({ node, src, alt, ...p }: any) => src ? (
    <span className="block my-4">
      <Image src={src} alt={alt ?? ''} width={800} height={600}
        sizes="(max-width: 896px) calc(100vw - 3rem), 800px"
        quality={65}
        className="max-w-full h-auto rounded-lg shadow-lg"
        unoptimized={!canOptimizeMagImage(src)} />
    </span>
  ) : null,
};

const MarkdownRenderer: React.FC<{ content: string; className?: string }> = ({ content, className = '' }) => (
  <div className={`markdown-content ${className}`}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {sanitize(content)}
    </ReactMarkdown>
  </div>
);

export default MarkdownRenderer;
